import React from 'react';
import { Spin } from 'antd';
import { PlusOutlined, TagOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { useProjectClientTickets } from '@hooks/projects/useProjectClientTickets';
import ClientTicketsKanbanBoard from '@/components/module/projects/client_tickets_tab/ClientTicketsKanbanBoard';
import ClientTicketDetailModal from '@/components/module/projects/client_tickets_tab/ClientTicketDetailModal';
import IssueUpgradeCreateModal from '@/components/module/projects/issues_upgrades_tab/IssueUpgradeCreateModal';
import {
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
    DetailPanelLoading,
    BoardKanbanBoardShell,
    BoardTabRoot,
    BoardPageTitle,
    BoardPageSubtitle,
} from '../style';

export default function ProjectClientTicketsTab({ projectId }) {
    const [selectedItem, setSelectedItem] = React.useState(null);
    const [selectedItemColumn, setSelectedItemColumn] = React.useState(null);
    const [limit, setLimit] = React.useState(100);
    const {
        activeRows,
        submittedRows,
        submittedToClientRows,
        acknowledgedRows,
        isLoading,
        canCreate,
        canView,
        canDrag,
        canUpdate,
        canAcknowledge,
        createModal,
        dropTarget,
        draggingColumn,
        handleDndDragStart,
        handleDndDragOverColumn,
        handleDndDragEnd,
        total: clientServerTotal,
    } = useProjectClientTickets(
        projectId,
        { page: 1, limit: limit },
        { mergeSubmittedToClientIntoSubmitted: true, mergeSubmittedIntoActive: true },
    );

    const loadedCount =
        (activeRows?.length || 0) +
        (submittedToClientRows?.length || 0) +
        (acknowledgedRows?.length || 0);
    const hasMore = loadedCount < clientServerTotal;

    if (!canView) {
        return (
            <EmptyStateBlock>
                <EmptyStateIcon>
                    <TagOutlined />
                </EmptyStateIcon>
                <EmptyStateTitle>Client Tickets</EmptyStateTitle>
                <EmptyStateDescription>You do not have permission to view this tab.</EmptyStateDescription>
            </EmptyStateBlock>
        );
    }

    return (
        <>
            {isLoading ? (
                <Container
                    style={{
                        margin: '26px clamp(16px, 4vw, 40px) 0 clamp(16px, 4vw, 40px)',
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <EmptyStateBlock>
                        <DetailPanelLoading>
                            <Spin size="large" />
                        </DetailPanelLoading>
                    </EmptyStateBlock>
                </Container>
            ) : (
                <Container $display="flex" $flex="1" $minHeight="0" $overflow="hidden">
                    <BoardTabRoot
                        style={{
                            flex: 1,
                            minWidth: '100%',
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            marginBottom: 0,
                            paddingBottom: 0,
                        }}
                    >
                        <BoardTabRoot
                            style={{
                                backgroundColor: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                marginBottom: 0,
                                marginRight: 24,
                                padding: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                                overflow: 'hidden',
                                '--board-kanban-inline-gutter': 'clamp(16px, 4vw, 40px)',
                            }}
                        >
                            <Container
                                $display="flex"
                                $justifyContent="space-between"
                                $alignItems="flex-start"
                                $gap="16px"
                                $flexWrap="wrap"
                                style={{ marginBottom: 24 }}
                            >
                                <div>
                                    <BoardPageTitle>Client Tickets</BoardPageTitle>
                                    <BoardPageSubtitle>
                                        Track tickets across active, submitted, and acknowledged stages.
                                    </BoardPageSubtitle>
                                </div>
                                {canCreate ? (
                                    <Button
                                        type="button"
                                        $primary
                                        $blackOnLight
                                        $size="13px"
                                        $padding="6px 14px"
                                        icon={<PlusOutlined />}
                                        onClick={createModal.openModal}
                                    >
                                        Create Ticket
                                    </Button>
                                ) : null}
                            </Container>
                            <BoardKanbanBoardShell>
                                <ClientTicketsKanbanBoard
                                    activeRows={activeRows}
                                    submittedRows={submittedToClientRows}
                                    acknowledgedRows={acknowledgedRows}
                                    canDrag={canDrag}
                                    canUpdate={canUpdate}
                                    canAcknowledge={canAcknowledge}
                                    dropTarget={dropTarget}
                                    draggingColumn={draggingColumn}
                                    onDndDragStart={handleDndDragStart}
                                    onDndDragOverColumn={handleDndDragOverColumn}
                                    onDndDragEnd={handleDndDragEnd}
                                    onSelectItem={(item, sourceColumn) => {
                                        setSelectedItem(item);
                                        setSelectedItemColumn(sourceColumn ?? null);
                                    }}
                                    hasMore={hasMore}
                                    onLoadMore={() => setLimit((l) => l + 100)}
                                />
                            </BoardKanbanBoardShell>
                        </BoardTabRoot>
                    </BoardTabRoot>
                </Container>
            )}
            <IssueUpgradeCreateModal
                form={createModal.form}
                open={createModal.modalOpen}
                onCancel={createModal.closeModal}
                onSubmit={createModal.handleSubmit}
                submitPending={createModal.createPending}
                pendingAttachments={createModal.pendingAttachments}
                pendingLinks={createModal.pendingLinks}
                handleFilesSelected={createModal.handleFilesSelected}
                removeAttachment={createModal.removeAttachment}
                handleLinkRequest={createModal.handleLinkRequest}
                handleLinkModalSubmit={createModal.handleLinkModalSubmit}
                linkModalOpen={createModal.linkModalOpen}
                setLinkModalOpen={createModal.setLinkModalOpen}
                linkModalForm={createModal.linkModalForm}
                linkModalCallbackRef={createModal.linkModalCallbackRef}
                mentionUsers={createModal.mentionUsers}
                mode="create"
                modalTitle="Create client ticket"
                showType={false}
                initialType="issue"
                descriptionPlaceholder="Describe the ticket. Add files, links, and @mentions if needed."
            />
            <ClientTicketDetailModal
                item={selectedItem}
                displayColumnId={selectedItemColumn}
                onClose={() => {
                    setSelectedItem(null);
                    setSelectedItemColumn(null);
                }}
            />
        </>
    );
}
